<script>
  import { uploadFile, signedUrl } from '../lib/aws.js'
  import { checkVideo } from '../lib/video_checks.js'
  import { postJSON } from '../lib/csrf.js'

  // Shows only the current participant's own uploads (fetched from
  // GET /uploads, backed by the Upload model — not a live bucket listing) plus
  // a widget for uploading a new video. `root` is where new uploads are
  // written in S3; it's per-user (see pages/files.html.erb) so filenames can't
  // collide across participants.
  let {
    bucket = 'ldc-slre',
    root = 'uploads/',
    checks = {},
    allowedDomains = [],
    videoLanguages = [], // [{ key, label }] — the languages this video may be in
    confirmations: CONFIRMATIONS = [], // [{ id, text }] — from Upload::CONFIRMATIONS
  } = $props()

  const base = root && !root.endsWith('/') ? `${root}/` : root

  // Temporarily off — flip back to true to disable uploading.
  const UPLOADS_DISABLED = false

  let uploads = $state([])
  let loading = $state(true)
  let error = $state(null)
  let fileInput

  // The confirmation checklist gates both submission routes: a participant
  // states which language the video is in, then confirms each requirement and
  // consents, before the file picker or the link form will accept anything.
  let videoLanguage = $state(videoLanguages.length === 1 ? videoLanguages[0].key : '')

  const languageLabel = $derived(
    videoLanguages.find((l) => l.key === videoLanguage)?.label ?? ''
  )

  let confirmed = $state({})

  // The server's wording, with the chosen language filled in.
  const confirmationText = (item) =>
    item.text.replace('%{language}', languageLabel || '…')

  // The language confirmation is meaningless until a language is chosen, so it
  // can't be ticked before then.
  const dependsOnLanguage = (item) => item.text.includes('%{language}')
  const confirmationDisabled = (item) => dependsOnLanguage(item) && !videoLanguage

  const allConfirmed = $derived(
    Boolean(videoLanguage) && CONFIRMATIONS.every((item) => confirmed[item.id])
  )

  // Changing the language retracts the statement that was made about the old
  // one — the participant has to read and tick it again.
  function setVideoLanguage(value) {
    videoLanguage = value
    for (const item of CONFIRMATIONS) if (dependsOnLanguage(item)) delete confirmed[item.id]
  }

  function resetConfirmations() {
    confirmed = {}
    if (videoLanguages.length !== 1) videoLanguage = ''
  }

  // Link submissions: a URL to a video hosted on one of the whitelisted sites,
  // fetched by a separate process later. Checked here for a quick answer; the
  // server re-checks against the same list, which is the one that counts.
  let linkUrl = $state('')
  let linkPhase = $state('idle') // idle | saving | done
  let linkError = $state(null)
  let linkSaved = $state(null)

  function linkHostAllowed(value) {
    let url
    try {
      url = new URL(value.trim())
    } catch {
      return { ok: false, reason: 'That doesn’t look like a valid link.' }
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:')
      return { ok: false, reason: 'Links must start with http:// or https://.' }
    // No list to check against (stale props, or the page was served before the
    // prop existed) — let it through and let the server, which is the
    // authoritative check, accept or reject it. Silently refusing every host
    // here would be a far more confusing failure.
    if (!allowedDomains.length) return { ok: true }

    const host = url.hostname.toLowerCase().replace(/\.$/, '')
    if (!allowedDomains.includes(host))
      return { ok: false, reason: `“${host}” isn’t one of the accepted video sites.` }
    return { ok: true }
  }

  async function saveLink() {
    linkError = null
    linkSaved = null
    const value = linkUrl.trim()
    if (!value) {
      linkError = 'Paste the link to your video first.'
      return
    }

    const verdict = linkHostAllowed(value)
    if (!verdict.ok) {
      linkError = verdict.reason
      return
    }

    const agreed = CONFIRMATIONS.map((item) => ({ id: item.id, text: confirmationText(item) }))

    linkPhase = 'saving'
    try {
      const res = await postJSON('/uploads', {
        upload: {
          source_url: value, language: videoLanguage, status: 'pending', confirmations: agreed,
        },
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        linkError = (data.errors || ['Could not save that link.']).join(' ')
        linkPhase = 'idle'
        return
      }
      linkSaved = value
      linkUrl = ''
      linkPhase = 'done'
      resetConfirmations()
      await loadUploads()
    } catch (e) {
      linkError = e.message || String(e)
      linkPhase = 'idle'
    }
  }

  // Upload state machine: idle | checking | uploading | done
  let phase = $state('idle')
  let md5Progress = $state(0)
  let report = $state(null) // { status, filename, failureReasons, durationSeconds, ... }

  const busy = $derived(phase === 'checking' || phase === 'uploading')

  async function guard(fn) {
    error = null
    try { await fn() } catch (e) { error = e.message || String(e) }
  }

  async function loadUploads() {
    loading = true
    await guard(async () => {
      const res = await fetch('/uploads', { headers: { Accept: 'application/json' } })
      if (!res.ok) throw new Error(`Failed to load your uploads (${res.status}).`)
      uploads = await res.json()
    })
    loading = false
  }

  async function onUpload(event) {
    const file = event.target.files?.[0]
    if (!file) return
    if (fileInput) fileInput.value = ''
    error = null
    report = null
    md5Progress = 0

    // 1. Automatic checks.
    phase = 'checking'
    let result
    try {
      result = await checkVideo(file, { ...checks, onMd5Progress: (p) => (md5Progress = p) })
    } catch (e) {
      error = e.message || String(e)
      phase = 'idle'
      return
    }

    const key = base + file.name

    // 2. Upload to S3 only if the checks passed.
    if (result.passed) {
      phase = 'uploading'
      try {
        await uploadFile(bucket, key, file)
      } catch (e) {
        result.passed = false
        result.failureReasons = [`Upload to S3 failed: ${e.message || e}`]
      }
    }

    const status = result.passed ? 'passed' : 'failed'

    // 3. Record the attempt (pass or fail) server-side.
    try {
      await postJSON('/uploads', {
        upload: {
          bucket, key,
          filename: result.filename,
          content_type: result.contentType,
          byte_size: result.byteSize,
          duration_seconds: result.durationSeconds,
          md5: result.md5,
          codec: result.codec,
          status,
          language: videoLanguage,
          confirmations: CONFIRMATIONS.map((item) => ({ id: item.id, text: confirmationText(item) })),
          failure_reasons: result.failureReasons,
        },
      })
    } catch (e) {
      console.error('Failed to record upload attempt', e)
    }

    report = { ...result, status }
    phase = 'done'
    resetConfirmations()
    await loadUploads()
  }

  // Link submissions have no S3 object — open the hosting site directly.
  async function open_(upload) {
    if (upload.source_url) {
      window.open(upload.source_url, '_blank', 'noopener')
      return
    }
    await guard(async () => {
      const url = await signedUrl(upload.bucket, upload.key)
      window.open(url, '_blank')
    })
  }

  const statusClass = (status) =>
    status === 'passed' ? 'bg-green-100 text-green-800'
    : status === 'pending' ? 'bg-amber-100 text-amber-800'
    : 'bg-red-100 text-red-800'

  const fmtSize = (n) =>
    n == null ? '' : n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1048576).toFixed(1)} MB`
  const fmtDuration = (s) => {
    if (s == null) return '—'
    const m = Math.floor(s / 60), sec = Math.round(s % 60)
    return `${m}:${String(sec).padStart(2, '0')}`
  }
  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })

  loadUploads()
</script>

<div class="max-w-2xl mx-auto">
  {#if error}
    <div class="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-800">{error}</div>
  {/if}

  <section class="mb-6 space-y-4 text-gray-700 leading-relaxed">
    <h2 class="text-lg font-semibold text-gray-900">Consent</h2>
    <p>
      The video recording you upload to this site will be included in a database that will be
      shared with individuals and organizations engaged in language-related research, education and
      technology development. Individuals and organizations that receive this material will first
      agree to use the database only for language-related research, education and technology
      development purposes. Names and emails collected for compensation and contact purposes are
      not distributed to individuals or organizations receiving the data. You can withdraw from the
      study or ask to have your data removed at any time by contacting study personnel.
    </p>
  </section>

  <section class="mb-6 rounded-md border border-gray-200 p-4">
    <h2 class="text-lg font-semibold text-gray-900">Please confirm</h2>
    <p class="mt-1 text-sm text-gray-600">
      Please confirm the following before uploading your video
    </p>

    {#if videoLanguages.length > 1}
      <label class="mt-3 block text-sm text-gray-700">
        <span class="block">Which language is this video in?</span>
        <select
          class="mt-1 border rounded max-w-xs"
          value={videoLanguage}
          onchange={(event) => setVideoLanguage(event.currentTarget.value)}
        >
          <option value="">Select…</option>
          {#each videoLanguages as language}
            <option value={language.key}>{language.label}</option>
          {/each}
        </select>
      </label>
    {/if}

    <ul class="mt-3 space-y-2">
      {#each CONFIRMATIONS as item}
        <li>
          <label
            class="flex items-start gap-2 text-gray-800"
            class:opacity-50={confirmationDisabled(item)}
          >
            <input
              type="checkbox"
              class="mt-1"
              checked={Boolean(confirmed[item.id])}
              disabled={confirmationDisabled(item)}
              onchange={(event) => (confirmed[item.id] = event.currentTarget.checked)}
            />
            <span>{confirmationText(item)}</span>
          </label>
        </li>
      {/each}
    </ul>

    {#if !allConfirmed}
      <p class="mt-3 text-sm text-gray-500">
        {videoLanguage
          ? 'Tick every box above to submit your video.'
          : 'Choose the language of your video, then tick every box above to submit it.'}
      </p>
    {/if}
  </section>

  <div class="mb-4 flex flex-col items-end gap-1">
    <label class="text-sm rounded-md bg-indigo-600 text-white px-3 py-1 hover:bg-indigo-500 {(busy || UPLOADS_DISABLED || !allConfirmed) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}">
      {#if phase === 'checking'}Checking… {Math.round(md5Progress * 100)}%
      {:else if phase === 'uploading'}Uploading…
      {:else}Upload video{/if}
      <input class="hidden" type="file" accept="video/*" bind:this={fileInput} onchange={onUpload} disabled={busy || UPLOADS_DISABLED || !allConfirmed} />
    </label>
    {#if UPLOADS_DISABLED}
      <p class="text-xs text-gray-500">Uploads are temporarily disabled.</p>
    {/if}
  </div>

  <!-- Link submission — an alternative to uploading the file itself. -->
  <div class="mb-6 rounded-md border border-gray-200 p-4">
    <h2 class="text-sm font-medium text-gray-800">Or save a link to your video</h2>
    <!-- The accepted sites are listed in the Instructions block above, so this
         doesn't repeat them; a rejected link names the offending site. -->
    <p class="mt-1 text-xs text-gray-500">
      Paste the link to your video on one of the sites listed above. We will download it for review.
    </p>
    <div class="mt-3 flex flex-wrap items-start gap-2">
      <input
        type="url"
        class="flex-1 min-w-[16rem] border rounded px-2 py-1 text-sm"
        placeholder="https://www.youtube.com/watch?v=…"
        bind:value={linkUrl}
        disabled={linkPhase === 'saving' || !allConfirmed}
        onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveLink() } }}
      />
      <button
        type="button"
        class="text-sm rounded-md bg-indigo-600 text-white px-3 py-1 hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed"
        onclick={saveLink}
        disabled={linkPhase === 'saving' || !allConfirmed}
      >
        {linkPhase === 'saving' ? 'Saving…' : 'Save link'}
      </button>
    </div>
    {#if linkError}
      <p class="mt-2 text-sm text-red-700">{linkError}</p>
    {/if}
    {#if linkSaved}
      <p class="mt-2 text-sm text-green-700">
        ✓ Link saved. It will be downloaded and reviewed; your progress updates once it passes.
      </p>
    {/if}
  </div>

  {#if report}
    {#if report.status === 'passed'}
      <div class="mb-4 rounded-md bg-green-50 ring-1 ring-green-200 p-4">
        <p class="font-semibold text-green-800">✓ &ldquo;{report.filename}&rdquo; uploaded successfully.</p>
        <dl class="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-green-900">
          <div><dt class="inline text-green-700">Size:</dt> {fmtSize(report.byteSize)}</div>
          <div><dt class="inline text-green-700">Duration:</dt> {fmtDuration(report.durationSeconds)}</div>
        </dl>
      </div>
    {:else}
      <div class="mb-4 rounded-md bg-red-50 ring-1 ring-red-200 p-4">
        <p class="font-semibold text-red-800">✗ Upload failed. Please try again.</p>
        <ul class="mt-2 list-disc list-inside text-sm text-red-900">
          {#each report.failureReasons as reason}<li>{reason}</li>{/each}
        </ul>
      </div>
    {/if}
  {/if}

  {#if loading}
    <p class="text-gray-500">Loading…</p>
  {:else if uploads.length === 0}
    <p class="text-gray-500">You haven&rsquo;t submitted any videos yet.</p>
  {:else}
    <ul class="divide-y border rounded-md">
      {#each uploads as upload}
        <li class="flex items-center justify-between gap-3 px-3 py-2">
          <div class="min-w-0">
            <button
              class="text-left text-indigo-600 hover:text-indigo-500 disabled:cursor-not-allowed disabled:text-gray-400 break-all"
              onclick={() => open_(upload)}
              disabled={!upload.source_url && upload.status !== 'passed'}
            >
              {upload.source_url ? '🔗' : '📄'} {upload.filename || upload.source_url}
            </button>
            <div class="text-xs text-gray-500">
              {fmtDate(upload.created_at)}
              {#if !upload.source_url}
                &middot; {fmtSize(upload.byte_size)} &middot; {fmtDuration(upload.duration_seconds)}
              {:else if upload.status === 'pending'}
                &middot; link &mdash; awaiting download and review
              {:else}
                &middot; link
              {/if}
            </div>
          </div>
          <span class="shrink-0 text-xs rounded-full px-2 py-0.5 {statusClass(upload.status)}">
            {upload.status}
          </span>
        </li>
      {/each}
    </ul>
  {/if}
</div>
